import { HttpClient } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import { forkJoin, map, Observable, of, tap } from 'rxjs';

import { API_V1_URL } from '../../../core/config/api.config';
import { AuthService } from '../../../core/services/auth.service';
import { KatalogArtikel } from '../models/katalog-artikel.model';
import { PositionStatus } from '../models/position-status.enum';
import { ReturnDetail } from '../models/return-detail.model';
import { ReturnPosition } from '../models/return-position.model';
import { ReturnStatus } from '../models/return-status.enum';
import { ReturnUnitOption } from '../models/return-unit-option.model';
import { Return } from '../models/return.model';
import { ReturnsLookupItem } from '../models/returns-lookup-item.model';
import { ArtikelEinheitDto, normalizeArtikelEinheiten, waehleArtikelEinheit } from '../../../shared/models/artikel-einheit.model';

type BackendReturnStatus =
  | 'Offen'
  | 'InBearbeitung'
  | 'In Bearbeitung'
  | 'Erfasst';

interface BackendReturnOverviewDto {
  filialNummer: number;
  filialName: string;
  datum: string;
  status: BackendReturnStatus;
  wert: number;
  quote: number;
  pos: number;
}

type BackendReturnUnitDto = ArtikelEinheitDto;

interface BackendReturnPositionDto {
  id: string | null;
  artikelnummer: number;
  artikelName: string;
  erfassteMenge: number;
  aktuelleMenge: number;
  abverkaufSeitAufnahme: number;
  status: number;
  aufnahmezeitpunkt: string | null;
  grundEinheitId: number;
  mengeEinheitId: number;
  verkaufspreis: number;
  einheiten: readonly BackendReturnUnitDto[];
}

interface BackendReturnDetailDto {
  filialNummer: number;
  datum: string;
  status: BackendReturnStatus;
  anteilTagesumsatz?: number | null;
  positionen: BackendReturnPositionDto[];
}

interface BackendAvailableReturnArticleDto {
  artikelnummer: number;
  artikelName: string;
  grundEinheitId: number;
  einheiten: readonly BackendReturnUnitDto[];
}

export interface SaveReturnPositionRequest {
  id?: string | null;
  artikelnummer: number;
  retoureMenge: number;
  mengeEinheitId: number;
}

export interface SavedReturnPosition {
  id: string | null;
  artikelnummer: number;
  retoureMenge: number;
  mengeEinheitId: number;
  geloescht?: boolean;
}

export interface ReturnLockSession {
  lockId: string;
  expiresAt: string;
}

@Injectable({ providedIn: 'root' })
export class ReturnsService {
  private readonly http = inject(HttpClient);
  private readonly authService = inject(AuthService);
  private readonly apiBaseUrl = API_V1_URL;
  private readonly activeLocks = new Map<string, ReturnLockSession>();
  private readonly anzahlOffenState = signal<number | undefined>(undefined);
  private readonly availableArticlesState = signal<readonly KatalogArtikel[]>([]);
  private readonly overviewByFiliale = new Map<number, Return>();
  private readonly unitNamesById = new Map<number, string>();
  private availableArticlesFilialNummer: number | null = null;

  readonly anzahlOffen = this.anzahlOffenState.asReadonly();

  getReturns(): Observable<Return[]> {
    return this.http
      .get<BackendReturnOverviewDto[]>(`${this.apiBaseUrl}/retouren`)
      .pipe(
        map((items) => items.map((item) => this.mapReturn(item))),
        tap((items) => {
          this.overviewByFiliale.clear();
          for (const item of items) this.overviewByFiliale.set(item.filialNummer, item);
        })
      );
  }

  getAnzahlOffen(): Observable<number> {
    return this.http
      .get<number>(`${this.apiBaseUrl}/retouren/anzahl-offen`)
      .pipe(tap((anzahl) => this.anzahlOffenState.set(anzahl)));
  }

  getReturnDetail(filialNummer: number): Observable<ReturnDetail> {
    return this.http
      .get<BackendReturnDetailDto>(`${this.apiBaseUrl}/retouren/${filialNummer}`)
      .pipe(map((item) => this.mapReturnDetail(item)));
  }

  loadAvailableArticles(filialNummer: number): Observable<KatalogArtikel[]> {
    if (this.availableArticlesFilialNummer === filialNummer) {
      return of([...this.availableArticlesState()]);
    }

    return this.http
      .get<BackendAvailableReturnArticleDto[]>(
        `${this.apiBaseUrl}/retouren/${filialNummer}/verfuegbare-artikel`
      )
      .pipe(
        map((items) =>
          items.map((item) => {
            const options = this.mapUnitOptions(
              item.einheiten,
              item.grundEinheitId,
              0
            );
            const option =
              waehleArtikelEinheit(options,
                options.some((entry) => entry.isDefault) ? null : item.grundEinheitId) ??
              this.unitOption(item.grundEinheitId, 0);

            return {
              artikelnummer: item.artikelnummer,
              artikelName: item.artikelName,
              grundEinheitId: item.grundEinheitId,
              einzelpreis: 0,
              einheit: option.einheit,
              verfuegbareEinheiten: options,
            } satisfies KatalogArtikel;
          })
        ),
        tap((items) => {
          this.availableArticlesFilialNummer = filialNummer;
          this.availableArticlesState.set(items);
        })
      );
  }


  acquireReturnLock(filialNummer: number): Observable<ReturnLockSession> {
    const key = this.returnLockKey(filialNummer);

    return this.http
      .post<ReturnLockSession>(`${this.apiBaseUrl}/locks/acquire`, {
        keys: [key],
      })
      .pipe(tap((session) => this.activeLocks.set(key, session)));
  }

  heartbeatReturnLock(filialNummer: number): Observable<ReturnLockSession | null> {
    const key = this.returnLockKey(filialNummer);
    const session = this.activeLocks.get(key);

    if (!session) return of(null);

    return this.http
      .put<ReturnLockSession>(
        `${this.apiBaseUrl}/locks/${session.lockId}/heartbeat`,
        null
      )
      .pipe(tap((refreshed) => this.activeLocks.set(key, refreshed)));
  }

  getReturnLockSession(filialNummer: number): ReturnLockSession | null {
    return this.activeLocks.get(this.returnLockKey(filialNummer)) ?? null;
  }

  releaseReturnLock(filialNummer: number): Observable<void> {
    const key = this.returnLockKey(filialNummer);
    const session = this.activeLocks.get(key);

    if (!session) return of(void 0);

    // /auth/logout releases every lock of the session on the backend. Once the
    // local auth token is gone, do not send a redundant/unauthorized DELETE.
    if (!this.authService.getToken()) {
      this.activeLocks.delete(key);
      return of(void 0);
    }

    return this.http
      .delete<void>(`${this.apiBaseUrl}/locks/${session.lockId}`)
      .pipe(tap(() => this.activeLocks.delete(key)));
  }

  releaseReturnLockBeforeUnload(filialNummer: number): void {
    const key = this.returnLockKey(filialNummer);
    const session = this.activeLocks.get(key);

    if (!session) return;

    const token = this.authService.getToken();
    this.activeLocks.delete(key);

    if (!token) return;

    const headers: Record<string, string> = {
      Authorization: `Bearer ${token}`,
    };

    void fetch(`${this.apiBaseUrl}/locks/${session.lockId}`, {
      method: 'DELETE',
      keepalive: true,
      headers,
    });
  }

  clearReturnLock(filialNummer: number): void {
    this.activeLocks.delete(this.returnLockKey(filialNummer));
  }

  savePositions(
    filialNummer: number,
    positions: readonly SaveReturnPositionRequest[]
  ): Observable<SavedReturnPosition[]> {
    const toCreate = positions.filter((position) => !position.id);
    const toUpdate = positions.filter((position) => Boolean(position.id));
    const requests: Observable<SavedReturnPosition[]>[] = [];

    if (toCreate.length > 0) {
      requests.push(
        this.http.post<SavedReturnPosition[]>(
          `${this.apiBaseUrl}/retouren/${filialNummer}`,
          toCreate
        )
      );
    }

    if (toUpdate.length > 0) {
      requests.push(
        this.http.put<SavedReturnPosition[]>(
          `${this.apiBaseUrl}/retouren/${filialNummer}`,
          toUpdate
        )
      );
    }

    if (requests.length === 0) return of([]);
    if (requests.length === 1) return requests[0]!;

    return forkJoin(requests).pipe(map((groups) => groups.flat()));
  }

  closeReturn(filialNummer: number, bestaetigt = false): Observable<void> {
    return this.http.post<void>(
      `${this.apiBaseUrl}/retouren/${filialNummer}/abschluss`,
      {},
      { params: { bestaetigt: String(bestaetigt) } }
    );
  }

  /** Legacy lookup helpers kept for the existing UI. */
  getGruende(): Observable<ReturnsLookupItem[]> {
    return of([]);
  }

  getEinheiten(): Observable<ReturnsLookupItem[]> {
    return of(
      [...this.unitNamesById.entries()].map(([id, bezeichnung]) => ({
        id,
        bezeichnung,
      }))
    );
  }

  searchKatalog(query: string, limit = 30): KatalogArtikel[] {
    const term = query.trim().toLowerCase();
    const all = this.availableArticlesState();

    if (!term) return all.slice(0, limit);

    return all
      .filter(
        (item) =>
          item.artikelName.toLowerCase().includes(term) ||
          String(item.artikelnummer).includes(term)
      )
      .slice(0, limit);
  }

  unitIdFromLabel(label: string): number | null {
    const normalized = label.trim().toLocaleLowerCase('de-DE');
    const cached = [...this.unitNamesById.entries()].find(
      ([, value]) => value.trim().toLocaleLowerCase('de-DE') === normalized
    );
    if (cached) return cached[0];

    return null;
  }

  private returnLockKey(filialNummer: number): string {
    return `retouren:${filialNummer}`;
  }

  private mapReturn(item: BackendReturnOverviewDto): Return {
    return {
      filialNummer: item.filialNummer,
      filialeName: item.filialName ?? '',
      datum: this.formatDate(item.datum),
      retourenTag: item.datum,
      status: this.mapStatus(item.status),
      anzahlPositionen: item.pos,
      wert: item.wert,
      quote: item.quote,
      quoteBasis: 0,
      hauptgrund: '',
      istUeberfaellig: false,
    };
  }

  private mapReturnDetail(item: BackendReturnDetailDto): ReturnDetail {
    const overview = this.overviewByFiliale.get(item.filialNummer);

    return {
      filialNummer: item.filialNummer,
      filialeName: overview?.filialeName ?? '',
      datum: this.formatDate(item.datum),
      retourenTag: item.datum,
      status: this.mapStatus(item.status),
      tagesumsatz: 0,
      anteilTagesumsatz:
        typeof item.anteilTagesumsatz === 'number' && Number.isFinite(item.anteilTagesumsatz)
          ? item.anteilTagesumsatz
          : null,
      positionen: item.positionen.map((position, index) => this.mapPosition(position, index)),
    };
  }

  private mapPosition(item: BackendReturnPositionDto, index: number): ReturnPosition {
    const currentUnitId = Number.isInteger(item.mengeEinheitId)
      ? item.mengeEinheitId
      : item.grundEinheitId;
    const options = this.mapUnitOptions(
      item.einheiten,
      currentUnitId,
      item.verkaufspreis
    );
    const option =
      waehleArtikelEinheit(options, currentUnitId) ??
      this.unitOption(currentUnitId, item.verkaufspreis);
    const menge = item.aktuelleMenge;

    return {
      rowId: item.id ?? `${item.artikelnummer}#${currentUnitId}#${index}`,
      positionId: item.id ?? null,
      artikelnummer: item.artikelnummer,
      artikelName: item.artikelName,
      grund: '',
      menge,
      einheit: option.einheit,
      mengeEinheitId: option.id ?? currentUnitId,
      grundEinheitId: item.grundEinheitId,
      einzelpreis: item.verkaufspreis,
      gesamtwert: Math.round(menge * item.verkaufspreis * 100) / 100,
      status: item.status === 1 ? PositionStatus.Erfasst : PositionStatus.Gemeldet,
      istNeu: false,
      verfuegbareEinheiten: options,
    };
  }

  private mapUnitOptions(
    items: readonly BackendReturnUnitDto[],
    currentUnitId: number,
    einzelpreis: number
  ): readonly ReturnUnitOption[] {
    const byId = new Map<number, ReturnUnitOption>();

    for (const item of normalizeArtikelEinheiten(items)) {
      this.unitNamesById.set(item.id, item.name);
      byId.set(item.id, {
        id: item.id,
        einheit: item.name,
        einzelpreis,
        menge: item.menge,
        baseEinheitId: item.baseEinheitId,
        nr: item.nr,
        isDefault: item.isDefault,
        faktorZurKleinstenEinheit: item.faktorZurKleinstenEinheit,
      });
    }

    if (!byId.has(currentUnitId)) {
      byId.set(currentUnitId, this.unitOption(currentUnitId, einzelpreis));
    }

    return [...byId.values()];
  }

  private unitName(id: number, fallback?: string): string {
    const cached = this.unitNamesById.get(id);
    if (cached) return cached;

    const normalizedFallback = fallback?.trim();
    if (
      normalizedFallback &&
      !/^Einheit\s+\d+$/i.test(normalizedFallback)
    ) {
      return normalizedFallback;
    }

    return `Einheit ${id}`;
  }

  private unitOption(id: number, einzelpreis: number): ReturnUnitOption & { id: number } {
    return {
      id,
      einheit: this.unitName(id),
      einzelpreis,
    };
  }

  private mapStatus(status: BackendReturnStatus): ReturnStatus {
    switch (status) {
      case 'Offen':
        return ReturnStatus.Offen;
      case 'InBearbeitung':
      case 'In Bearbeitung':
        return ReturnStatus.InBearbeitung;
      case 'Erfasst':
        return ReturnStatus.Erfasst;
    }
  }

  private formatDate(value: string): string {
    if (!value) return '';
    const date = new Date(`${value}T00:00:00`);
    if (Number.isNaN(date.getTime())) return value;
    return new Intl.DateTimeFormat('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    }).format(date);
  }
}
